import {
  METRIC_GROUPS,
  type MetricView,
  type ValuationConfidence,
  type ValuationWarning,
  type ValueRange,
} from '@mfos/shared';
import { Badge } from '../../components/Badge';
import { formatDate, formatDateTime } from '../../lib/format';
import { isNegativeAmount } from '../finance/finance-display';
import {
  CONFIDENCE,
  GROUP_LABELS,
  ORIGIN_LABELS,
  QUALITY,
  WARNING_SEVERITY,
  formatMetricValue,
  formatWhole,
} from './valuation-display';
import { phrase } from '../../lib/i18n/runtime';

/** Shown wherever a value appears: the estimate is never a formal valuation. */
export function ValuationDisclaimer({ text }: { text: string }) {
  return (
    <p className="notice notice--info" role="note">
      <span aria-hidden="true">i </span>
      <strong>{phrase('Not a formal valuation.')}</strong> {text}
    </p>
  );
}

export function ConfidenceBadge({
  confidence,
  score,
}: {
  confidence: ValuationConfidence;
  score?: number;
}) {
  const display = CONFIDENCE[confidence];
  return (
    <Badge
      tone={display.tone}
      label={score === undefined ? display.label : `${display.label} (${String(score)}/100)`}
    />
  );
}

/** Whole currency units; negative values carry a spoken cue, not only a minus sign. */
export function WholeMoney({ amount, currency }: { amount: string; currency: string }) {
  return (
    <span className="numeric">
      {formatWhole(amount)} {currency}
      {isNegativeAmount(amount) && <span className="visually-hidden"> (negative)</span>}
    </span>
  );
}

export function RangeText({ range, currency }: { range: ValueRange; currency: string }) {
  return (
    <span>
      <WholeMoney amount={range.low} currency={currency} /> –{' '}
      <WholeMoney amount={range.high} currency={currency} /> (base{' '}
      <WholeMoney amount={range.base} currency={currency} />)
    </span>
  );
}

export function RangeFigures({
  range,
  currency,
  label,
}: {
  range: ValueRange;
  currency: string;
  label: string;
}) {
  return (
    <dl className="stat-grid" aria-label={label}>
      <div>
        <dt>{phrase('Low')}</dt>
        <dd>
          <WholeMoney amount={range.low} currency={currency} />
        </dd>
      </div>
      <div>
        <dt>{phrase('Base')}</dt>
        <dd>
          <WholeMoney amount={range.base} currency={currency} />
        </dd>
      </div>
      <div>
        <dt>{phrase('High')}</dt>
        <dd>
          <WholeMoney amount={range.high} currency={currency} />
        </dd>
      </div>
    </dl>
  );
}

export function QualityBadge({ quality }: { quality: MetricView['quality'] }) {
  return <Badge tone={QUALITY[quality].tone} label={QUALITY[quality].label} />;
}

export function ValuationWarnings({ warnings }: { warnings: readonly ValuationWarning[] }) {
  if (warnings.length === 0) return <p className="meta">{phrase('No data-quality warnings.')}</p>;
  return (
    <ul className="plain-list stack" aria-label={phrase('Data-quality warnings')}>
      {warnings.map((warning) => (
        <li key={`${warning.code}-${warning.metricKey ?? ''}-${warning.message}`}>
          <Badge
            tone={WARNING_SEVERITY[warning.severity].tone}
            label={WARNING_SEVERITY[warning.severity].label}
          />{' '}
          {warning.message}
        </li>
      ))}
    </ul>
  );
}

/** Where a figure comes from: origin, source reference, timestamp and any note. */
export function MetricSource({
  metric,
  timeZone,
}: {
  metric: MetricView;
  timeZone?: string | undefined;
}) {
  return (
    <>
      <span>{phrase(ORIGIN_LABELS[metric.origin])}</span>
      {metric.sourceReference !== null && <div className="meta">{metric.sourceReference}</div>}
      {metric.sourceTimestamp !== null && (
        <div className="meta">
          {metric.origin === 'MANUAL'
            ? `As of ${formatDate(metric.sourceTimestamp.slice(0, 10))}`
            : `Read ${formatDateTime(metric.sourceTimestamp, timeZone)}`}
        </div>
      )}
      {metric.note !== null && <div className="meta">{metric.note}</div>}
    </>
  );
}

/** Metrics grouped as in the catalog, each with its value, quality and lineage. */
export function MetricTable({
  metrics,
  caption,
  timeZone,
}: {
  metrics: readonly MetricView[];
  caption: string;
  timeZone?: string | undefined;
}) {
  const groups = METRIC_GROUPS.map((group) => ({
    group,
    items: metrics.filter((metric) => metric.group === group),
  })).filter((entry) => entry.items.length > 0);
  return (
    <div className="table-scroll">
      <table className="table table--compact">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{phrase('Metric')}</th>
            <th scope="col" className="numeric">
              {phrase('Value')}
            </th>
            <th scope="col">{phrase('Quality')}</th>
            <th scope="col">{phrase('Source')}</th>
          </tr>
        </thead>
        {groups.map(({ group, items }) => (
          <tbody key={group}>
            <tr>
              <th scope="colgroup" colSpan={4} className="table__group">
                {phrase(GROUP_LABELS[group])}
              </th>
            </tr>
            {items.map((metric) => (
              <tr key={metric.key}>
                <th scope="row">{metric.label}</th>
                <td className="numeric">{formatMetricValue(metric)}</td>
                <td>
                  <QualityBadge quality={metric.quality} />
                </td>
                <td>
                  <MetricSource metric={metric} timeZone={timeZone} />
                </td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
